import L from 'leaflet';
import { agregarSuperposicion } from './controlDeCapas';

/**
 * Maquinaria común de las capas de clima que se animan.
 *
 * El radar y el satélite hacen lo mismo con datos distintos: piden varios
 * cuadros del pasado reciente, los encienden por turnos para que se vea el
 * movimiento, y muestran la hora del que está en pantalla. Lo único que
 * cambia es de dónde sale cada cuadro y cómo se pinta.
 *
 * Vivían duplicadas. Se juntaron aquí porque el arreglo del z-index —que
 * costó encontrar— habría que hacerlo dos veces, y la segunda se olvida.
 */

/** Milisegundos por cuadro durante la animación. */
const PASO_MS = 450;

/** Pausa extra al llegar al cuadro más reciente, para poder leerlo. */
const PAUSA_FINAL_MS = 1600;

/**
 * Altura de las capas de clima en la pila.
 *
 * Sin esto la capa se ve sólo a veces, y el motivo es traicionero: todas las
 * capas de mosaicos nacen con z-index 1, así que entre iguales gana el orden
 * en que se agregaron. La ortofoto del cliente llega DESPUÉS —hay que ir a
 * preguntarla al servidor— y al llegar se plantaba encima, tapándolo todo.
 * O sea que fallaba justo donde más importa: en el cliente que subió su
 * imagen. El satélite va debajo del radar porque es el fondo de contexto.
 */
export const Z_SATELITE = 380;
export const Z_RADAR = 400;

export interface OpcionesCapaAnimada {
  /** Minutos hacia atrás de cada cuadro, del más viejo al más nuevo. */
  cuadros: number[];
  /** Dirección del mosaico para un cuadro dado. */
  urlDe: (minutosAtras: number) => string;
  /** Nombre en el control de la esquina. */
  etiqueta: string;
  /** Arma la línea del reloj, ya traducida. */
  textoHora: (hora: string) => string;
  opacidad: number;
  maxNativeZoom: number;
  zIndex: number;
  atribucion: string;
  /** Llave de localStorage donde se recuerda si está prendida. */
  preferencia: string;
  /** Clase CSS del contenedor, para efectos de pintado. */
  clase?: string;
  /** Si no hay preferencia guardada, ¿arranca prendida? */
  porOmision: boolean;
}

/** Cajita con la hora del cuadro que se está viendo. */
const ControlReloj = L.Control.extend({
  options: { position: 'bottomleft' as L.ControlPosition },
  onAdd() {
    const caja = L.DomUtil.create('div', 'clima-reloj');
    caja.style.cssText =
      'background:var(--white);color:var(--dark);border:1px solid var(--border);' +
      'border-radius:6px;padding:3px 8px;font-size:11px;font-weight:600;' +
      'box-shadow:0 1px 3px rgba(0,0,0,.15);line-height:1.5;white-space:nowrap';
    return caja;
  },
});

/**
 * Monta una capa animada sobre el mapa.
 *
 * @returns función de limpieza, para llamar al desmontar el mapa.
 */
export function agregarCapaAnimada(map: L.Map, o: OpcionesCapaAnimada): () => void {
  const grupo = L.layerGroup();

  // Todos los cuadros se cargan a la vez y se muestran cambiando la
  // transparencia, no agregando y quitando capas. Alternar capas hace que
  // la imagen parpadee en cada paso, porque Leaflet vuelve a pedir los
  // mosaicos; así la animación corre limpia después de la primera vuelta.
  const capas = o.cuadros.map((minutos, i) =>
    L.tileLayer(o.urlDe(minutos), {
      opacity: i === o.cuadros.length - 1 ? o.opacidad : 0,
      maxNativeZoom: o.maxNativeZoom,
      maxZoom: 21,
      zIndex: o.zIndex,
      className: o.clase,
      attribution: o.atribucion,
      // Los mosaicos sin dato llegan vacíos o faltan; esto evita el ícono
      // de imagen rota regado por todo el mapa.
      errorTileUrl: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
    }),
  );
  capas.forEach(c => grupo.addLayer(c));

  const reloj = new ControlReloj();
  let temporizador: ReturnType<typeof setTimeout> | null = null;
  let actual = o.cuadros.length - 1;

  const pintarReloj = () => {
    const nodo = reloj.getContainer();
    if (!nodo) return;
    const cuando = new Date(Date.now() - o.cuadros[actual]! * 60000);
    nodo.textContent = o.textoHora(
      cuando.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
    );
  };

  const avanzar = () => {
    capas[actual]!.setOpacity(0);
    actual = (actual + 1) % capas.length;
    capas[actual]!.setOpacity(o.opacidad);
    pintarReloj();
    // El cuadro más reciente se queda más tiempo: es el que importa, y sin
    // la pausa el ciclo se ve como un parpadeo sin principio ni fin.
    temporizador = setTimeout(avanzar, actual === capas.length - 1 ? PAUSA_FINAL_MS : PASO_MS);
  };

  const arrancar = () => {
    if (temporizador) return;
    reloj.addTo(map);
    pintarReloj();
    temporizador = setTimeout(avanzar, PAUSA_FINAL_MS);
  };

  const parar = () => {
    if (temporizador) clearTimeout(temporizador);
    temporizador = null;
    reloj.remove();
  };

  const alPrender = (e: L.LayersControlEvent) => {
    if (e.layer !== grupo) return;
    localStorage.setItem(o.preferencia, 'si');
    arrancar();
  };
  const alApagar = (e: L.LayersControlEvent) => {
    if (e.layer !== grupo) return;
    localStorage.setItem(o.preferencia, 'no');
    parar();
  };

  // Con la pestaña en segundo plano nadie está viendo la animación, pero
  // seguiría pidiendo mosaicos cada medio segundo. Se congela y se reanuda.
  const alCambiarVisibilidad = () => {
    if (!map.hasLayer(grupo)) return;
    if (document.hidden) parar();
    else arrancar();
  };

  map.on('overlayadd', alPrender);
  map.on('overlayremove', alApagar);
  document.addEventListener('visibilitychange', alCambiarVisibilidad);

  const quitar = agregarSuperposicion(map, grupo, o.etiqueta);

  const guardada = localStorage.getItem(o.preferencia);
  if (guardada === 'si' || (guardada === null && o.porOmision)) {
    grupo.addTo(map);
    arrancar();
  }

  return () => {
    parar();
    map.off('overlayadd', alPrender);
    map.off('overlayremove', alApagar);
    document.removeEventListener('visibilitychange', alCambiarVisibilidad);
    quitar();
  };
}
